import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { addComponent, updateComponent, updateComponentPosition } from '../redux/editorSlice';
// import './ComponentRenderer.css';
import ComponentRenderer from './ComponentRenderer';
import { COMPONENT_TYPES } from '../constants';

const RowComponent = ({ content, style, data, children, id, components = [] }) => {
  const dispatch = useDispatch();

  // 기본 스타일과 사용자 스타일 병합
  const mergedStyle = {
    width: data && data.width ? data.width : '100%',
    display: 'flex',
    flexDirection: 'row', // 가로 방향으로 배치 (열이 가로로 나열됨)
    minHeight: 'auto', // 자동 높이 사용 (부모에 맞춰짐)
    height: '100%', // 부모 높이에 맞춤
    position: 'relative',
    padding: '0',
    flex: '1 1 auto', // 부모 영역을 채움
    boxSizing: 'border-box',
    backgroundColor: style.backgroundColor || 'rgba(247, 250, 252, 0.5)',
    border: style.border || '1px solid #e2e8f0',
    borderRadius: style.borderRadius || '4px',
    paddingLeft: style.paddingLeft || '0',
    paddingRight: style.paddingRight || '0',
    paddingTop: style.paddingTop || '0',
    paddingBottom: style.paddingBottom || '0',
    overflow: 'hidden', // 내용물이 넘치지 않도록
    gap: '0', // 간격 제거
    margin: '0', // 마진 제거
    ...style
  };

  // gutter 속성 처리
  if (data && data.gutter && parseInt(data.gutter) > 0) {
    // 사용자 지정 gutter가 있을 때만 적용
    mergedStyle.gap = `${data.gutter}px`;
  } else {
    // 기본 간격은 0
    mergedStyle.gap = '0';
  }

  // 정렬 속성 처리 - 가로 방향에 맞게 수정
  if (data && data.horizontalAlign) {
    switch (data.horizontalAlign) {
      case 'left':
        mergedStyle.justifyContent = 'flex-start';
        break;
      case 'center':
        mergedStyle.justifyContent = 'center';
        break;
      case 'right':
        mergedStyle.justifyContent = 'flex-end';
        break;
      case 'between':
        mergedStyle.justifyContent = 'space-between';
        break;
      case 'around':
        mergedStyle.justifyContent = 'space-around';
        break;
      default:
        break;
    }
  }

  if (data && data.verticalAlign) {
    switch (data.verticalAlign) {
      case 'top':
        mergedStyle.alignItems = 'flex-start';
        break;
      case 'middle':
        mergedStyle.alignItems = 'center';
        break;
      case 'bottom':
        mergedStyle.alignItems = 'flex-end';
        break;
      case 'stretch':
        mergedStyle.alignItems = 'stretch';
        break;
      default:
        break;
    }
  }

  // 이 Row에 속한 자식 컴포넌트 필터링
  const childComponents = components.filter(comp => comp.parentId === id);
  
  // 자식 컴포넌트 너비 자동 분할 계산
  useEffect(() => {
    // 자식 컴포넌트 (열 컴포넌트 포함 모든 컴포넌트)
    if (childComponents.length > 0) {
      
      // 각 자식에게 동일한 너비 비율 할당 (간격 없음)
      const widthPercent = 100 / childComponents.length;
      
      // 각 자식 컴포넌트 업데이트
      childComponents.forEach((child, index) => {
        // 현재 데이터 가져오기
        const currentData = child.data || {};
        const currentStyle = child.style || {};
        
        // 자식 컴포넌트 유형에 따라 스타일 조정
        let newStyle = {
          ...currentStyle,
          width: `${widthPercent}%`,
          maxWidth: `${widthPercent}%`,
          flex: `1 ${widthPercent}`, // flex-grow:1로 설정하여 공간을 채우도록
          height: '100%', // 행 컴포넌트의 높이에 맞춤
          margin: '0',
          padding: '0', // 패딩 제거
          overflow: 'hidden', // 내용물이 넘치지 않도록
          boxSizing: 'border-box', // 패딩과 보더가 크기에 포함되도록
          minWidth: '0', // 최소 너비 제한 제거
        };
        
        // 열 컴포넌트일 경우 추가 스타일 적용
        if (child.type === COMPONENT_TYPES.COLUMN) {
          newStyle = {
            ...newStyle,
            display: 'flex',
            flexDirection: 'column',
          };
        }
        
        const newData = {
          ...currentData,
          width: `${widthPercent}%`
        };
        
        // 변경이 필요한지 확인 (변경을 최소화하기 위해)
        if (
          currentStyle.width !== newStyle.width ||
          currentStyle.maxWidth !== newStyle.maxWidth ||
          currentStyle.flex !== newStyle.flex ||
          currentStyle.height !== newStyle.height ||
          currentStyle.margin !== newStyle.margin ||
          currentStyle.padding !== newStyle.padding ||
          currentStyle.overflow !== newStyle.overflow ||
          currentStyle.boxSizing !== newStyle.boxSizing ||
          currentStyle.minWidth !== newStyle.minWidth ||
          currentData.width !== newData.width
        ) {
          dispatch(updateComponent({
            id: child.id,
            changes: {
              style: newStyle,
              data: newData
            }
          }));
        }
      });
    }
  }, [childComponents, dispatch, id]);

  // 드롭 기능 추가
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'COMPONENT',
    hover: (item, monitor) => {
      if (!item.id) return; // 새 컴포넌트는 처리하지 않음
      
      const offset = monitor.getClientOffset();
      if (!offset) return;
      
      // 현재 Row 엘리먼트의 위치 정보
      const rowEl = document.getElementById(`row-${id}`);
      if (!rowEl) return;
      
      const rowRect = rowEl.getBoundingClientRect();
      const y = offset.y - rowRect.top;
      
      // 현재 위치에 있는 컴포넌트 찾기
      const hoveredComponent = findComponentAtPosition(childComponents, 0, y);
      
      if (hoveredComponent && hoveredComponent.id !== item.id) {
        // 사용하지 않는 상태 업데이트 제거
        // const componentEl = document.getElementById(`component-${hoveredComponent.id}`);
        // if (componentEl) {
        //   const rect = componentEl.getBoundingClientRect();
        //   const middle = rect.top + (rect.height / 2);
        //   const position = offset.y < middle ? 'above' : 'below';
        //   
        //   setDragOverIndex(hoveredComponent.order || 0);
        //   setDragPosition(position);
        // }
      } else {
        // 빈 공간에 드래그 중
        // setDragOverIndex(null);
        // setDragPosition(null);
      }
    },
    drop: (item, monitor) => {
      // 이미 다른 곳에서 처리된 경우
      if (monitor.didDrop()) {
        return;
      }


      // 행 컴포넌트는 중첩될 수 없음
      if (item.type === COMPONENT_TYPES.ROW) {
        return;
      }
      
      // 직접 컴포넌트 타입 확인 + 카테고리 확인
      const dropComponentType = item.type;
      const category = item.category || 'unknown';
      
      
      const offset = monitor.getClientOffset();
      if (!offset) return;

      // 현재 row 엘리먼트의 위치 정보
      const rowEl = document.getElementById(`row-${id}`);
      if (!rowEl) return;
      
      const rowRect = rowEl.getBoundingClientRect();
      
      // row 내부의 상대적 위치
      let x = offset.x - rowRect.left;
      let y = offset.y - rowRect.top;
      
      // 이미 있는 컴포넌트 위치 업데이트인 경우
      if (item.id) {
        // 현재 컴포넌트가 자신의 자식으로 드래그되는 것을 방지
        if (item.id === id) {
          return;
        }
        
        // 부모-자식 순환 참조 방지
        if (item.type === COMPONENT_TYPES.ROW || item.type === COMPONENT_TYPES.COLUMN) {
          // 현재 Row가 드래그하는 컴포넌트의 자식인지 확인
          const isChildOfDraggedComponent = checkIsChild(components, item.id, id);
          if (isChildOfDraggedComponent) {
            return; // 순환 참조가 발생하므로 드롭 취소
          }
        }
        
        // 드롭 위치에 있는 컴포넌트 찾기
        const componentAtDropPosition = findComponentAtPosition(childComponents, x, y);
        
        let insertIndex;
        
        // 다른 컴포넌트 위에 드롭한 경우
        if (componentAtDropPosition) {
          
          // 자기 자신 위에 드롭한 경우 무시
          if (componentAtDropPosition.id === item.id) {
            return;
          }
          
          // 드롭된 컴포넌트의 현재 순서
          const draggedComponent = childComponents.find(c => c.id === item.id);
          const currentOrder = draggedComponent ? draggedComponent.order || 0 : -1;
          const targetOrder = componentAtDropPosition.order || 0;
          
          // 드롭 위치에 있는 컴포넌트의 위아래 여부 판단
          const componentEl = document.getElementById(`component-${componentAtDropPosition.id}`);
          if (componentEl) {
            const componentRect = componentEl.getBoundingClientRect();
            const componentMiddleY = componentRect.top + (componentRect.height / 2);
            
            if (offset.y < componentMiddleY) {
              // 컴포넌트 위쪽에 드롭 - 해당 컴포넌트 위로 배치
              insertIndex = targetOrder;
              
              // 이미 부모의 자식인 경우 
              if (draggedComponent) {
                if (currentOrder > targetOrder) {
                  // 위로 이동 - 사이의 컴포넌트들은 한 칸씩 아래로
                  childComponents.forEach(comp => {
                    if (comp.id !== item.id && 
                        (comp.order || 0) >= targetOrder && 
                        (comp.order || 0) < currentOrder) {
                      dispatch(updateComponentPosition({
                        id: comp.id,
                        newPosition: comp.position,
                        order: (comp.order || 0) + 1
                      }));
                    }
                  });
                }
              } else {
                // 모든 컴포넌트의 순서 조정
                childComponents.forEach(comp => {
                  if ((comp.order || 0) >= targetOrder) {
                    dispatch(updateComponentPosition({
                      id: comp.id,
                      newPosition: comp.position,
                      order: (comp.order || 0) + 1
                    }));
                  }
                });
              }
            } else {
              // 컴포넌트 아래쪽에 드롭 - 해당 컴포넌트 아래로 배치
              insertIndex = targetOrder + 1;
              
              if (draggedComponent) {
                // 이미 부모의 자식인 경우
                if (currentOrder < targetOrder) {
                  // 아래로 이동 - 사이의 컴포넌트들은 한 칸씩 위로
                  childComponents.forEach(comp => {
                    if (comp.id !== item.id && 
                        (comp.order || 0) > currentOrder && 
                        (comp.order || 0) <= targetOrder) {
                      dispatch(updateComponentPosition({
                        id: comp.id,
                        newPosition: comp.position,
                        order: (comp.order || 0) - 1
                      }));
                    }
                  });
                }
              } else {
                // 외부에서 오는 경우, targetOrder보다 큰 컴포넌트들의 순서 업데이트
                childComponents.forEach(comp => {
                  if ((comp.order || 0) >= insertIndex) {
                    dispatch(updateComponentPosition({
                      id: comp.id,
                      newPosition: comp.position,
                      order: (comp.order || 0) + 1
                    }));
                  }
                });
              }
            }
          }
          
          
          // 컴포넌트 위치 업데이트
          dispatch(updateComponentPosition({
            id: item.id,
            parentId: id,
            newPosition: { x: 0, y: 0 },
            order: insertIndex || 0
          }));
        } else {
          // 빈 공간에 드롭한 경우 - 마지막 순서 찾기
          const maxOrder = childComponents.length > 0 
            ? Math.max(...childComponents.map(c => c.order || 0)) 
            : -1;
          
          
          // 컴포넌트 위치 업데이트
          dispatch(updateComponentPosition({
            id: item.id,
            parentId: id,
            newPosition: { x: 0, y: 0 },
            order: maxOrder + 1
          }));
        }
      } else {
        // 새 컴포넌트 추가
        const newId = uuidv4();
        
        // 새 컴포넌트의 순서 계산 - 마지막 순서 + 1
        const maxOrder = childComponents.length > 0 
          ? Math.max(...childComponents.map(c => c.order || 0)) 
          : -1;
        
        
        // 기본 크기와 스타일 설정 - 행 내부 컴포넌트는 높이를 최적화
        const defaultSize = { width: '100%', height: 40 };
        
        // 컴포넌트 타입에 따라 기본 높이 설정
        if (dropComponentType === COMPONENT_TYPES.IMAGE) {
          defaultSize.height = 120;
        } else if (dropComponentType === COMPONENT_TYPES.CONTAINER) {
          defaultSize.height = 150;
        } else if (dropComponentType === COMPONENT_TYPES.TEXT) {
          defaultSize.height = 40;
        } else if (dropComponentType === COMPONENT_TYPES.BUTTON) {
          defaultSize.height = 40;
        }
        
        // 컴포넌트 추가
        dispatch(addComponent({
          id: newId,
          type: dropComponentType,
          position: { x: 0, y: 0 },
          size: defaultSize,
          parentId: id,
          order: maxOrder + 1,
          category // 카테고리 속성 추가
        }));
      }
      
      return { id };
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }), [childComponents, id, components]);

  return (
    <div
      id={`row-${id}`}
      ref={drop}
      className={`row-component ${isOver ? 'drag-over' : ''}`}
      style={mergedStyle}
      data-component-type="ROW"
    >
      {/* 항상 표시되는 배경 메시지, 위치를 absolute로 설정하여 겹치지 않도록 함 */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        pointerEvents: 'none', // 클릭이 통과하도록 설정
        zIndex: 0 // 낮은 z-index로 설정
      }}>
        <span style={{color: '#999', fontStyle: 'italic'}}>
          {childComponents.length === 0 ? '여기에 컴포넌트를 추가하세요' : ''}
        </span>
      </div>
      
      {/* 자식 컴포넌트 렌더링 */}
      {childComponents
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(comp => (
          <ComponentRenderer 
            key={comp.id} 
            component={comp}
            components={components}
          />
        ))}
    </div>
  );
};

// 부모-자식 관계 체크
const checkIsChild = (components, parentId, childId) => {
  // 직계 자식 체크
  const isDirectChild = components.some(comp => comp.id === childId && comp.parentId === parentId);
  if (isDirectChild) return true;
  
  // 하위 자식들 체크 (재귀적으로)
  const children = components.filter(comp => comp.parentId === parentId);
  return children.some(child => checkIsChild(components, child.id, childId));
};

// 지정된 위치에 있는 컴포넌트 찾기
const findComponentAtPosition = (components, x, y) => {
  // 각 컴포넌트의 위치와 크기 확인
  for (const comp of components) {
    const el = document.getElementById(`component-${comp.id}`);
    if (el) {
      const rect = el.getBoundingClientRect();
      
      // 영역 내에 있는지 확인
      if (y >= rect.top && y <= rect.bottom) {
        return comp;
      }
    }
  }
  
  return null;
};

RowComponent.propTypes = {
  content: PropTypes.any,
  style: PropTypes.object,
  data: PropTypes.object,
  children: PropTypes.node,
  id: PropTypes.string.isRequired,
  components: PropTypes.array
};

export default RowComponent; 